import { useParams } from 'react-router-dom';
import { useTransactions } from 'store/transactions/useTransactions';
import {
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  GridItem,
  SimpleGrid,
  Flex
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { IFundRewardTokenForm } from 'components/onboarding/deployment/type';
import { yupResolver } from '@hookform/resolvers/yup';
import { fundRewardTokenSchema } from 'components/onboarding/deployment/formSchema';
import { FormError } from 'components/onboarding/form/FormError';
import { ProgramToken } from 'types';
import { useTokenBalance } from 'hooks/useTokenBalance';
import { FC } from 'react';
import {
  mapPowerSwitchStatusToDescription,
  PowerSwitchStatus
} from 'api/blockchain/rewardProgram';
import { ethers } from 'ethers';

type Props = {
  rewardToken: ProgramToken;
  rewardProgramDataMethod: string;
  rewardProgramFundMethod: string;
  programState?: PowerSwitchStatus;
  aludelAbi: ethers.ContractInterface;
};

export const FundRewardTokenForm: FC<Props> = ({
  rewardToken,
  rewardProgramDataMethod,
  rewardProgramFundMethod,
  programState,
  aludelAbi
}) => {
  const { fundAludel } = useTransactions();
  const { rewardProgramAddress } =
    useParams<{ rewardProgramAddress: string }>();

  const {
    watch,
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<IFundRewardTokenForm>({
    resolver: yupResolver(fundRewardTokenSchema),
    mode: 'onBlur'
  });

  const { insufficientBalance, nullAmount } = useTokenBalance(
    rewardToken,
    watch('rewardTokenAmount', 0).toString()
  );

  const renderError = () => {
    if (insufficientBalance && !nullAmount) {
      return <FormError>Insufficient balance</FormError>;
    }

    if (errors.rewardTokenAmount) {
      return <FormError>{errors.rewardTokenAmount.message}</FormError>;
    }
  };

  return (
    <>
      <Heading size='md' mb={6}>
        Fund Reward Token ({rewardToken.tokenSymbol})
      </Heading>
      <Text color='gray.200' mb={6}>
        Funding a reward token is a 2 transaction process. First the Aludel must
        be approved to 'spend' the token from your wallet, then the aludel
        `fund` function needs to be called in order to withdraw the token from
        your wallet; adding it to the reward pool and storing the accounting
        information.
      </Text>
      <Text color='gray.200' mb={6}>
        Token approvals are only for the exact amount that you're funding. If an
        approval already exists which is greater than the amount being funded,
        the step will be skipped.
      </Text>
      <SimpleGrid columns={[1, 2, 3]} gap={6}>
        <GridItem>
          <FormControl
            isInvalid={!!errors.rewardTokenAmount || insufficientBalance}
          >
            <FormLabel>Enter amount</FormLabel>
            <Input
              id='rewardTokenAmount'
              type='number'
              step='any'
              borderColor={'gray.300'}
              placeholder='0'
              {...register('rewardTokenAmount')}
            />
            {renderError()}
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isInvalid={!!errors.unlockDays}>
            <FormLabel>Enter unlock days</FormLabel>

            <Controller
              control={control}
              name='unlockDays'
              render={({ field: { onChange, value } }) => (
                <NumberInput
                  value={value}
                  onChange={(valueAsString) => onChange(valueAsString)}
                  min={0}
                >
                  <Input
                    as={NumberInputField}
                    id='unlockDays'
                    type='number'
                    borderColor={'gray.300'}
                    placeholder='0'
                    defaultValue={0}
                    {...register('unlockDays')}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              )}
            />

            {errors.unlockDays && (
              <FormError>{errors.unlockDays.message}</FormError>
            )}
          </FormControl>
        </GridItem>
        <GridItem>
          <Flex height='100%' alignItems='flex-end'>
            <Button
              borderRadius={'lg'}
              px={6}
              disabled={
                programState === PowerSwitchStatus.OFFLINE ||
                programState === PowerSwitchStatus.SHUTDOWN ||
                !!errors.unlockDays ||
                !!errors.rewardTokenAmount
              }
              onClick={handleSubmit((d) =>
                fundAludel(
                  rewardProgramAddress,
                  d.rewardTokenAmount,
                  d.unlockDays,
                  rewardProgramDataMethod,
                  rewardProgramFundMethod,
                  aludelAbi
                )
              )}
            >
              {programState === PowerSwitchStatus.OFFLINE ||
              programState === PowerSwitchStatus.SHUTDOWN
                ? mapPowerSwitchStatusToDescription[programState]
                : 'Fund'}
            </Button>
          </Flex>
        </GridItem>
      </SimpleGrid>
    </>
  );
};
